import cn from 'classnames'
import React, { FC, useCallback, useEffect } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Modal from '../Modal'
import { Store } from '../../../redux/store'
import Title from '../Title'
import api from '../../../utils/api'
import { showMigrationModal } from '../../../redux/slices/app'
import { UserMigrationResponse } from '../../../utils/types'
import { PortalAccountDomain } from '../../../utils/enums'

import tickIcon from './img/tick-icon.svg'
import styles from './style.module.scss'

const languageString = "dashboard.migration."

interface MigrationProps {
  className?: string
}

const Migration: FC<MigrationProps> = ({ className }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const migration = useSelector((state: Store) => state.App.migration, shallowEqual)
  const isMigrationModalShown = useSelector((state: Store) => state.App.isMigrationModalShown, shallowEqual)

  useEffect(() => {
    if (migration) {
      if (
        migration.isEligible
        && !(['ACCEPT', 'DECLINE'] as UserMigrationResponse[]).includes(migration.userMigrationResponse as any)
        && migration.toOrg === PortalAccountDomain.TMSY
      ) {
        dispatch(showMigrationModal(true))
      }
    }
  }, [dispatch, migration])

  const handleAction = useCallback(async (action: UserMigrationResponse) => {
    dispatch(showMigrationModal(false))
    await api.setMigrationResponse(action)
  }, [dispatch])

  const onModalClose = useCallback(() => {
    dispatch(showMigrationModal(false))
    handleAction('DEFER')
  }, [dispatch, handleAction])

  const handleCheckValues = () => {
    return <img className={styles.icon} src={tickIcon} alt="Tick icon" />
  }

  const currentRegulator = [{
    caption: t(`${languageString}regulator`),
    value: t(`${languageString}currentRegulator`)
  }, {
    caption: t(`${languageString}leverage`),
    value: t(`${languageString}currentLeverage`)
  }, {
    caption: t(`${languageString}negativeBalanceProtection`),
    value: handleCheckValues()
  }, {
    caption: t(`${languageString}clientsFundsSegregation`),
    value: handleCheckValues()
  }]

  const nextRegulator = [{
    caption: t(`${languageString}regulator`),
    value: t(`${languageString}nextRegulator`)
  }, {
    caption: t(`${languageString}leverage`),
    value: t(`${languageString}nextLeverage`)
  }, {
    caption: t(`${languageString}negativeBalanceProtection`),
    value: handleCheckValues()
  }, {
    caption: t(`${languageString}clientsFundsSegregation`),
    value: handleCheckValues()
  }]

  return isMigrationModalShown ? (
    <Modal
      isOpen={isMigrationModalShown}
      onModalClose={onModalClose}
      showCross
      shouldCloseOnEsc={false}
      className={styles['migration-modal']}

    >
      <div className={cn(styles.migration, className)}>
        <Title title={t(`${languageString}title`)} />
        <div className={styles.content} dangerouslySetInnerHTML={{ __html: t(`${languageString}generalContent`) }} />

        <div className={styles.options}>
          <div className={styles.option}>
            <div className={styles['option-title']}>{t(`${languageString}currentAccount`)}</div>
            {currentRegulator.map((item, index) => (
              <div key={index} className={styles['option-row']}>
                <span className={styles['caption']}>{item.caption}</span>
                <span>{item.value}</span>
              </div>
            ))}
          </div>
          <div className={cn(styles.option, styles.selected)}>
            <div className={styles['option-title']}>{t(`${languageString}optionToMigrate`)}</div>
            {nextRegulator.map((item, index) => (
              <div key={index} className={styles['option-row']}>
                <span className={styles['caption']}>{item.caption}</span>
                <span>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles['legal-content']} dangerouslySetInnerHTML={{ __html: t(`${languageString}legalContent`) }} />
        <div className={styles.footer}>
          <button
            className="green"
            type="button"
            onClick={() => handleAction('ACCEPT')}
          >
            {t(`${languageString}accept`)}
          </button>

          <span
            className={styles['green-link']}
            onClick={() => handleAction('DECLINE')}
          >
            {t(`${languageString}remindLater`)}
          </span>
        </div>
      </div>
    </Modal>
  ) : null
}

export default Migration